import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Plus,
  Pencil,
  Trash2,
  GraduationCap,
  RefreshCw,
  AlertTriangle,
  X,
} from "lucide-react";
import UpcomingCourseLoader from "./UpcomingCourseLoader";

const UpcomingCourseList = () => {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ============================================================
  // DELETE CONFIRMATION MODAL
  // ============================================================
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    course: null,
  });

  // ============================================================
  // FETCH UPCOMING COURSES
  // ============================================================
  const fetchUpcomingCourses = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/upcoming-courses`
      );

      setCourses(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching upcoming courses:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to fetch upcoming courses."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpcomingCourses();
  }, [fetchUpcomingCourses]);

  // ============================================================
  // CLOSE DELETE MODAL WITH ESCAPE
  // ============================================================
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && deleteModal.open && !deletingId) {
        setDeleteModal({
          open: false,
          course: null,
        });
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [deleteModal.open, deletingId]);

  // ============================================================
  // TOGGLE ACTIVE / INACTIVE STATUS
  // ============================================================
  const handleStatusToggle = async (course) => {
    if (!course?._id) return;

    // Prevent multiple requests
    if (updatingId || deletingId) return;

    try {
      setUpdatingId(course._id);

      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/upcoming-courses/${course._id}/status`
      );

      const updatedCourse = response.data?.data;

      if (!updatedCourse) {
        throw new Error("Invalid response from server.");
      }

      setCourses((prevCourses) =>
        prevCourses.map((item) =>
          item._id === course._id
            ? {
                ...item,
                isActive: updatedCourse.isActive,
              }
            : item
        )
      );

      toast.success(
        updatedCourse.isActive
          ? "Course activated successfully."
          : "Course deactivated successfully."
      );
    } catch (error) {
      console.error("Error updating course status:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to update course status."
      );
    } finally {
      setUpdatingId(null);
    }
  };

  // ============================================================
  // OPEN DELETE CONFIRMATION MODAL
  // ============================================================
  const handleDelete = (course) => {
    if (!course?._id) return;

    if (updatingId || deletingId) return;

    setDeleteModal({
      open: true,
      course,
    });
  };

  // ============================================================
  // CLOSE DELETE MODAL
  // ============================================================
  const handleCancelDelete = () => {
    if (deletingId) return;

    setDeleteModal({
      open: false,
      course: null,
    });
  };

  // ============================================================
  // CONFIRM DELETE COURSE
  // ============================================================
  const handleConfirmDelete = async () => {
    const course = deleteModal.course;

    if (!course?._id) return;

    try {
      setDeletingId(course._id);

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/upcoming-courses/${course._id}`
      );

      setCourses((prevCourses) =>
        prevCourses.filter((item) => item._id !== course._id)
      );

      setDeleteModal({
        open: false,
        course: null,
      });

      toast.success("Upcoming course deleted successfully.");
    } catch (error) {
      console.error("Error deleting upcoming course:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to delete upcoming course."
      );
    } finally {
      setDeletingId(null);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================
  if (loading) {
    return (
      <div className="w-full px-6 py-6">
        <UpcomingCourseLoader />
      </div>
    );
  }

  // ============================================================
  // UI
  // ============================================================
  return (
    <>
      <div className="w-full px-6 py-6">
        {/* ======================================================
            HEADER
        ====================================================== */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-7">
          {/* Heading */}
          <div>
            <h1 className="text-4xl font-bold text-primaryTextColor leading-tight">
              Upcoming Courses
            </h1>

            <p className="text-sm text-secondaryTextColor mt-1">
              Manage courses that are coming soon.
            </p>
          </div>

          {/* Header Actions */}
          <div className="flex items-center gap-3">
          

            {/* Add Course */}
            <button
              type="button"
              onClick={() => navigate("/upcoming-courses/add")}
              disabled={updatingId || deletingId}
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                px-7
                py-3
                rounded-xl
                bg-primaryBtnColor
                text-white
                text-base
                font-semibold
                shadow-sm
                hover:opacity-90
                hover:shadow-md
                transition-all
                duration-200
                disabled:opacity-50
                disabled:cursor-not-allowed
              "
            >
              <Plus size={19} />
              Add Course
            </button>
          </div>
        </div>

        {/* ======================================================
            EMPTY STATE
        ====================================================== */}
        {courses.length === 0 ? (
          <div
            className="
              w-full
              bg-white
              border
              border-gray-200
              rounded-2xl
              shadow-sm
              px-6
              py-12
              text-center
            "
          >
            <div
              className="
                mx-auto
                w-12
                h-12
                flex
                items-center
                justify-center
                rounded-xl
                bg-blue-50
                text-blue-600
                mb-4
              "
            >
              <GraduationCap size={24} />
            </div>

            <h3 className="text-lg font-semibold text-primaryTextColor">
              No Upcoming Courses
            </h3>

            <p className="text-sm text-secondaryTextColor mt-1 mb-5">
              Add your first upcoming course to get started.
            </p>

            <button
              type="button"
              onClick={() => navigate("/upcoming-courses/add")}
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                px-5
                py-2.5
                rounded-xl
                bg-primaryBtnColor
                text-white
                text-sm
                font-semibold
                hover:opacity-90
                hover:shadow-md
                transition-all
                duration-200
              "
            >
              <Plus size={17} />
              Add Course
            </button>
          </div>
        ) : (
          /* ====================================================
             COURSE GRID
          ==================================================== */
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {courses.map((course) => {
              const isUpdating = updatingId === course._id;
              const isDeleting = deletingId === course._id;

              return (
                <div
                  key={course._id}
                  className="
                    group
                    relative
                    overflow-hidden
                    bg-white
                    border
                    border-gray-200
                    rounded-2xl
                    shadow-[0_2px_8px_rgba(15,23,42,0.04)]
                    hover:shadow-[0_12px_30px_rgba(15,23,42,0.08)]
                    hover:border-gray-300
                    transition-all
                    duration-300
                  "
                >
                  {/* Premium Top Accent */}
                  <div
                    className="
                      absolute
                      top-0
                      left-0
                      right-0
                      h-0.5
                      bg-gradient-to-r
                      from-blue-600
                      via-indigo-500
                      to-blue-400
                      opacity-0
                      group-hover:opacity-100
                      transition-opacity
                      duration-300
                    "
                  />

                  {/* ==================================================
                      CARD CONTENT
                  ================================================== */}
                  <div className="p-5">
                    {/* ==================================================
                        TOP SECTION
                    ================================================== */}
                    <div className="flex items-start justify-between gap-4">
                      {/* Icon + Course Details */}
                      <div className="flex items-start gap-3.5 min-w-0">
                        {/* Course Icon */}
                        <div
                          className="
                            shrink-0
                            w-11
                            h-11
                            flex
                            items-center
                            justify-center
                            rounded-xl
                            bg-gradient-to-br
                            from-blue-50
                            to-indigo-50
                            text-blue-700
                            border
                            border-blue-100
                            shadow-sm
                            group-hover:shadow-md
                            transition-all
                            duration-300
                          "
                        >
                          <GraduationCap
                            size={21}
                            strokeWidth={2.2}
                          />
                        </div>

                        {/* Course Name + Type */}
                        <div className="min-w-0 pt-0.5">
                          {/* Course Name */}
                          <h3
                            className="
                              text-[17px]
                              font-bold
                              leading-snug
                              text-primaryTextColor
                              tracking-[-0.01em]
                              truncate
                            "
                            title={course.name}
                          >
                            {course.name}
                          </h3>

                          {/* Upcoming Course Badge */}
                          <div className="mt-2">
                            <span
                              className="
                                inline-flex
                                items-center
                                px-2
                                py-0.5
                                rounded
                                bg-orange-50
                                border
                                border-orange-200
                                text-[9px]
                                font-semibold
                                uppercase
                                tracking-[0.05em]
                                text-orange-600
                                leading-none
                              "
                            >
                              Upcoming Course
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* ==================================================
                          ACTIVE / INACTIVE BADGE
                      ================================================== */}
                      <span
                        className={`
                          shrink-0
                          inline-flex
                          items-center
                          gap-1.5
                          px-2.5
                          py-1.5
                          rounded-full
                          text-[11px]
                          font-semibold
                          border

                          ${
                            course.isActive
                              ? `
                                bg-emerald-50
                                text-emerald-600
                                border-emerald-100
                              `
                              : `
                                bg-gray-50
                                text-gray-500
                                border-gray-200
                              `
                          }
                        `}
                      >
                        <span
                          className={`
                            w-1.5
                            h-1.5
                            rounded-full

                            ${
                              course.isActive
                                ? "bg-emerald-500"
                                : "bg-gray-400"
                            }
                          `}
                        />

                        {course.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    {/* ==================================================
                        DIVIDER
                    ================================================== */}
                    <div className="border-t border-gray-100 my-5" />

                    {/* ==================================================
                        BOTTOM ACTIONS
                    ================================================== */}
                    <div className="flex items-center justify-between gap-3">
                      {/* ==================================================
                          ACTIVE / INACTIVE TOGGLE
                      ================================================== */}
                      <button
                        type="button"
                        onClick={() => handleStatusToggle(course)}
                        disabled={isUpdating || isDeleting}
                        aria-label={
                          course.isActive
                            ? "Deactivate course"
                            : "Activate course"
                        }
                        aria-pressed={course.isActive}
                        className="
                          group/status
                          flex
                          items-center
                          gap-2.5
                          text-xs
                          font-medium
                          text-gray-600
                          hover:text-primaryColor
                          transition-colors
                          disabled:opacity-50
                          disabled:cursor-not-allowed
                        "
                      >
                        {/* Toggle */}
                        <span
                          className={`
                            relative
                            inline-flex
                            shrink-0
                            w-10
                            h-5
                            rounded-full
                            transition-all
                            duration-200

                            ${
                              course.isActive
                                ? "bg-secondaryColor shadow-sm"
                                : "bg-gray-300"
                            }
                          `}
                        >
                          {/* Toggle Knob */}
                          <span
                            className={`
                              absolute
                              top-0.5
                              left-0.5
                              w-4
                              h-4
                              bg-white
                              rounded-full
                              shadow-sm
                              transition-transform
                              duration-200

                              ${
                                course.isActive
                                  ? "translate-x-5"
                                  : "translate-x-0"
                              }
                            `}
                          />
                        </span>

                        {/* Status Text */}
                        <span className="font-medium">
                          {isUpdating
                            ? "Updating..."
                            : course.isActive
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </button>

                      {/* ==================================================
                          ACTION BUTTONS
                      ================================================== */}
                      <div className="flex items-center gap-1.5">
                        {/* Edit */}
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/upcoming-courses/edit/${course._id}`
                            )
                          }
                          disabled={isDeleting || isUpdating}
                          className="
                            w-8
                            h-8
                            flex
                            items-center
                            justify-center
                            rounded-lg
                            text-blue-600
                            bg-blue-50
                            hover:bg-blue-100
                            transition-all
                            duration-200
                            disabled:opacity-50
                            disabled:cursor-not-allowed
                          "
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>

                        {/* Delete */}
                        <button
                          type="button"
                          onClick={() => handleDelete(course)}
                          disabled={isDeleting || isUpdating}
                          className="
                            w-8
                            h-8
                            flex
                            items-center
                            justify-center
                            rounded-lg
                            text-red-500
                            bg-red-50
                            hover:bg-red-100
                            transition-all
                            duration-200
                            disabled:opacity-50
                            disabled:cursor-not-allowed
                          "
                          title="Delete"
                        >
                          {isDeleting ? (
                            <span
                              className="
                                w-4
                                h-4
                                border-2
                                border-gray-300
                                border-t-red-500
                                rounded-full
                                animate-spin
                              "
                            />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ============================================================
          PREMIUM DELETE CONFIRMATION MODAL
      ============================================================ */}
      {deleteModal.open && deleteModal.course && (
        <div
          className="
            fixed
            inset-0
            z-[9999]
            flex
            items-center
            justify-center
            p-4
          "
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-course-title"
        >
          {/* Backdrop */}
          <div
            className="
              absolute
              inset-0
              bg-black/45
              backdrop-blur-[3px]
              animate-[fadeIn_180ms_ease-out]
            "
            onClick={handleCancelDelete}
          />

          {/* Modal */}
          <div
            className="
              relative
              w-full
              max-w-[420px]
              overflow-hidden
              rounded-2xl
              bg-white
              border
              border-gray-200
              shadow-[0_25px_70px_rgba(15,23,42,0.22)]
              animate-[modalIn_220ms_ease-out]
            "
            onClick={(event) => event.stopPropagation()}
          >
            {/* Premium top accent */}
            <div
              className="
                absolute
                top-0
                left-0
                right-0
                h-[3px]
                bg-gradient-to-r
                from-primaryBtnColor
                via-red-500
                to-orange-400
              "
            />

            {/* Close Button */}
            <button
              type="button"
              onClick={handleCancelDelete}
              disabled={!!deletingId}
              className="
                absolute
                top-4
                right-4
                w-8
                h-8
                flex
                items-center
                justify-center
                rounded-lg
                text-gray-400
                hover:text-gray-700
                hover:bg-gray-100
                transition-all
                duration-200
                disabled:opacity-40
                disabled:cursor-not-allowed
              "
              aria-label="Close"
            >
              <X size={17} />
            </button>

            {/* Modal Content */}
            <div className="px-6 pt-7 pb-6">
              {/* Warning Icon */}
              <div className="flex justify-center mb-5">
                <div
                  className="
                    relative
                    w-14
                    h-14
                    flex
                    items-center
                    justify-center
                    rounded-2xl
                    bg-red-50
                    border
                    border-red-100
                    text-red-500
                  "
                >
                  <div
                    className="
                      absolute
                      inset-0
                      rounded-2xl
                      bg-red-100/40
                      animate-pulse
                    "
                  />

                  <AlertTriangle
                    size={25}
                    strokeWidth={2}
                    className="relative"
                  />
                </div>
              </div>

              {/* Heading */}
              <div className="text-center">
                <h2
                  id="delete-course-title"
                  className="
                    text-xl
                    font-bold
                    text-primaryTextColor
                    tracking-[-0.02em]
                  "
                >
                  Delete Upcoming Course?
                </h2>

                <p
                  className="
                    mt-2
                    text-sm
                    leading-6
                    text-secondaryTextColor
                    px-2
                  "
                >
                  Are you sure you want to delete this course?
                  This action cannot be undone.
                </p>
              </div>

              {/* Course Preview */}
              <div
                className="
                  mt-5
                  flex
                  items-center
                  gap-3
                  rounded-xl
                  border
                  border-gray-200
                  bg-gray-50
                  px-4
                  py-3
                "
              >
                <div
                  className="
                    shrink-0
                    w-10
                    h-10
                    flex
                    items-center
                    justify-center
                    rounded-lg
                    bg-white
                    border
                    border-gray-200
                    text-blue-600
                    shadow-sm
                  "
                >
                  <GraduationCap
                    size={20}
                    strokeWidth={2.1}
                  />
                </div>

                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400">
                    Upcoming Course
                  </p>

                  <p
                    className="
                      mt-0.5
                      text-sm
                      font-semibold
                      text-primaryTextColor
                      truncate
                    "
                    title={deleteModal.course.name}
                  >
                    {deleteModal.course.name}
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-3 mt-6">
                {/* Cancel */}
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  disabled={!!deletingId}
                  className="
                    flex-1
                    h-11
                    rounded-xl
                    border
                    border-gray-200
                    bg-white
                    text-gray-700
                    text-sm
                    font-semibold
                    hover:bg-gray-50
                    hover:border-gray-300
                    transition-all
                    duration-200
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                >
                  Cancel
                </button>

                {/* Delete */}
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={!!deletingId}
                  className="
                    flex-1
                    h-11
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    bg-red-500
                    text-white
                    text-sm
                    font-semibold
                    shadow-sm
                    hover:bg-red-600
                    hover:shadow-md
                    transition-all
                    duration-200
                    disabled:opacity-70
                    disabled:cursor-not-allowed
                  "
                >
                  {deletingId ? (
                    <>
                      <span
                        className="
                          w-4
                          h-4
                          border-2
                          border-white/40
                          border-t-white
                          rounded-full
                          animate-spin
                        "
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete Course
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Modal Animations */}
          <style>
            {`
              @keyframes fadeIn {
                from {
                  opacity: 0;
                }
                to {
                  opacity: 1;
                }
              }

              @keyframes modalIn {
                from {
                  opacity: 0;
                  transform: translateY(12px) scale(0.97);
                }
                to {
                  opacity: 1;
                  transform: translateY(0) scale(1);
                }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
};

export default UpcomingCourseList;